import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
  arrayUnion,
  getDocs,
  collection,
} from "firebase/firestore";
import { db } from "../firebase";

export interface CleaningGuestSummary {
  avgRating: number;
  cleanliness: number;
  communication: number;
  cooperation: number;
  followedRules: number;
  behaviour: number;
  totalReviews: number;
  lastUpdated: string;
}

export interface CleaningHostGuestRating {
  guestId: string;
  reservationId?: string;
  hostId: string;
  dateCreated: string;
  cleanliness: number;
  communication: number;
  cooperation: number;
  followedRules: number;
  behaviour: number;
  overallRating: "Excellent" | "Superb" | "Very Nice" | "Worth It" | "Not Worth It";
  hostReview: string;
}

export interface CleaningGuestRatingState {
  summaries: Record<string, CleaningGuestSummary | null>;
  ratings: Record<string, CleaningHostGuestRating[]>;
  loading: boolean;
  error: string | null;
  reviewed: boolean;
  currentReview: CleaningHostGuestRating | null;
}

const initialState: CleaningGuestRatingState = {
  ratings: {},
  loading: false,
  error: null,
  reviewed: false,
  currentReview: null,
  summaries: {},
};

export const submitCleaningGuestRating = createAsyncThunk(
  "cleaningGuestRatings/submit",
  async ({
    guestId,
    reservationId,
    hostId,
    hostReview,
    overallRating,
    criteria,
  }: {
    guestId: string;
    reservationId: string;
    hostId: string;
    hostReview: string;
    overallRating: string;
    criteria: Record<string, number>;
  }) => {
    const ref = doc(db, "cleaningGuestRatings", guestId);
    const snapshot = await getDoc(ref);

    const newRating: CleaningHostGuestRating = {
      guestId,
      reservationId,
      hostId,
      hostReview,
      overallRating: overallRating as CleaningHostGuestRating["overallRating"],
      cleanliness:    criteria["Cleanliness"]    || 0,
      communication:  criteria["Communication"]  || 0,
      cooperation:    criteria["Cooperation"]    || 0,
      followedRules:  criteria["Followed Rules"] || 0,
      behaviour:      criteria["Behaviour"]      || 0,
      dateCreated: Timestamp.now().toDate().toISOString(),
    };

    if (snapshot.exists()) {
      const existing = snapshot.data();
      const alreadyReviewed = existing.ratings?.some(
        (r: any) => r.reservationId === reservationId
      );
      if (alreadyReviewed) return { reviewed: true, guestId };

      await updateDoc(ref, { ratings: arrayUnion(newRating) });
    } else {
      await setDoc(ref, { guestId, ratings: [newRating] });
    }

    return { reviewed: false, guestId };
  }
);

export const fetchCleaningGuestRatingsByGuest = createAsyncThunk<
  { guestId: string; ratings: CleaningHostGuestRating[] },
  string,
  { rejectValue: string }
>("cleaningGuestRatings/fetchByGuest", async (guestId, { rejectWithValue }) => {
  try {
    const ref = doc(db, "cleaningGuestRatings", guestId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { guestId, ratings: [] };
    return { guestId, ratings: snap.data().ratings || [] };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const fetchCleaningGuestRatingByReservation = createAsyncThunk<
  CleaningHostGuestRating | null,
  { guestId: string; reservationId: string },
  { rejectValue: string }
>(
  "cleaningGuestRatings/fetchByReservation",
  async ({ guestId, reservationId }, { rejectWithValue }) => {
    try {
      const ref = doc(db, "cleaningGuestRatings", guestId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      const ratings: CleaningHostGuestRating[] = snap.data().ratings || [];
      return ratings.find((r) => r.reservationId === reservationId) || null;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchAllCleaningGuestRatings = createAsyncThunk(
  "cleaningGuestRatings/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const snapshot = await getDocs(collection(db, "cleaningGuestRatings"));
      const allRatings: Record<string, CleaningHostGuestRating[]> = {};
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        allRatings[data.guestId] = data.ratings || [];
      });
      return allRatings;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const cleaningGuestRatingSlice = createSlice({
  name: "cleaningGuestRatings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(submitCleaningGuestRating.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitCleaningGuestRating.fulfilled, (state, action) => {
        state.reviewed = action.payload.reviewed;
        state.loading = false;
      })
      .addCase(submitCleaningGuestRating.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(fetchCleaningGuestRatingsByGuest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCleaningGuestRatingsByGuest.fulfilled, (state, action) => {
        const { guestId, ratings } = action.payload;
        state.ratings[guestId] = ratings;
        state.loading = false;
      })
      .addCase(fetchCleaningGuestRatingsByGuest.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(fetchAllCleaningGuestRatings.fulfilled, (state, action) => {
        state.ratings = action.payload;
        state.loading = false;
      })
      .addCase(
        fetchCleaningGuestRatingByReservation.fulfilled,
        (state, action: PayloadAction<CleaningHostGuestRating | null>) => {
          state.currentReview = action.payload;
        }
      );
  },
});

export default cleaningGuestRatingSlice.reducer;
